/**
 * The six step components that make up the booking wizard.
 *
 * Each step is a presentational component: it receives data + selection
 * callbacks from the parent `BookingPage`, renders its UI, and never owns
 * cross-step state. This keeps the data-flow obvious and unit-testable.
 *
 * The CSS in this project uses an inverted dark-mode convention, so the
 * parent must always pass an `isVisuallyDark` flag (i.e. `!isDarkMode`)
 * via `palette` from `getBookingPalette()`.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  pickAddress,
  pickTranslation,
  resolveStorageUrl,
} from "../../helper/bookingApi";
import { resolveImageUrl } from "./BookingTheme";
import {
  ArrowRightIcon,
  BOOKING_FONT_FAMILY,
  EmptyState,
  RetryButton,
  SearchInput,
  SelectableCard,
  SkeletonRow,
  StepCard,
  StepFooter,
  StepHeader,
  STEP_PAD,
} from "./BookingPrimitives";

/* =====================================================================
 * Step 1 — Industry (organization-type)
 * ===================================================================== */

export function IndustryStep({ palette, lang, industries, loading, error, selectedId, onSelect, onRetry }) {
  const { t } = useTranslation();
  return (
    <StepCard palette={palette}>
      <StepHeader
        palette={palette}
        title={t("booking.industry.title")}
        subtitle={t("booking.industry.subtitle")}
      />
      <div
        style={{
          padding: `${STEP_PAD.contentTop} ${STEP_PAD.x} ${STEP_PAD.contentBottom} ${STEP_PAD.x}`,
        }}
      >
        {loading ? (
          <SkeletonRow palette={palette} count={5} />
        ) : error ? (
          <EmptyState
            palette={palette}
            icon="las la-exclamation-circle"
            title={t("booking.errors.loadIndustries")}
            action={<RetryButton palette={palette} onClick={onRetry} />}
          />
        ) : industries.length === 0 ? (
          <EmptyState
            palette={palette}
            icon="las la-folder-open"
            title={t("booking.common.noResults")}
          />
        ) : (
          <div className="d-grid gap-2">
            {industries.map((industry) => {
              const name = pickTranslation(industry, lang);
              const img = resolveImageUrl(industry.image_path);
              return (
                <SelectableCard
                  key={industry.id}
                  palette={palette}
                  selected={selectedId === industry.id}
                  onClick={() => onSelect(industry)}
                  iconImg={img || null}
                  icon={img ? null : "las la-tag"}
                  title={name}
                  trailing={<ArrowRightIcon color={palette.textSubtle} size={18} />}
                />
              );
            })}
          </div>
        )}
      </div>
    </StepCard>
  );
}

/* =====================================================================
 * Step 2 — Organization (filtered by selected industry)
 *
 * Layout: responsive card grid (1 col mobile / 2 col tablet / 3 col xl).
 * Each card surfaces the org's identity AND a small preview of the first
 * branches when the API returns them, so users can shortcut-click a
 * specific branch and skip Step 3 entirely. This matches the density of
 * the designer's mockup while keeping click targets explicit.
 * ===================================================================== */

const BRANCH_PREVIEW_COUNT = 3;

function makeBookingToken(id, label) {
  if (id == null) return "";
  const safeLabel = String(label || id)
    .trim()
    .replace(/[\\/]+/g, "-")
    .replace(/\s+/g, "-");
  return `${id},${encodeURIComponent(safeLabel || id)}`;
}

function makeOrganizationPublicHref(org, lang, name) {
  const base = lang === "en" ? "/en/online-booking/org" : "/online-booking/org";
  return `${base}/${makeBookingToken(org.id, name)}`;
}

function getOrgBranches(org) {
  if (!org) return [];
  if (Array.isArray(org.branches)) return org.branches;
  if (Array.isArray(org.branch)) return org.branch;
  return [];
}

function getOrgBranchCount(org) {
  if (org?.branches_count != null) return org.branches_count;
  if (org?.branch_count != null) return org.branch_count;
  return getOrgBranches(org).length;
}

function OrgBranchRow({ palette, branch, lang, onClick }) {
  const name = pickTranslation(branch, lang);
  const address = pickAddress(branch);
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      className="text-start w-100 d-flex align-items-start gap-2"
      style={{
        background: "transparent",
        border: "none",
        padding: "8px 10px",
        borderRadius: 8,
        cursor: "pointer",
        color: palette.text,
        transition: "background 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = palette.surfaceHover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      <i
        className="las la-map-marker-alt"
        style={{
          color: palette.accent,
          fontSize: "1rem",
          marginTop: 2,
          flexShrink: 0,
        }}
        aria-hidden="true"
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          className="d-block fw-600"
          style={{
            fontSize: "0.85rem",
            color: palette.text,
            lineHeight: 1.25,
            wordBreak: "break-word",
          }}
        >
          {name}
        </span>
        {address ? (
          <span
            className="d-block"
            style={{
              fontSize: "0.75rem",
              color: palette.textMuted,
              marginTop: 1,
              wordBreak: "break-word",
            }}
          >
            {address}
          </span>
        ) : null}
      </span>
      <span style={{ marginTop: 4 }}>
        <ArrowRightIcon color={palette.textSubtle} size={16} />
      </span>
    </button>
  );
}

function OrgCard({
  palette,
  org,
  lang,
  selected,
  onSelectBranch,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const name = pickTranslation(org, lang);
  const img = resolveImageUrl(org.image_path);
  const publicHref = makeOrganizationPublicHref(org, lang, name);
  const branches = getOrgBranches(org);
  const branchCount = getOrgBranchCount(org);
  const preview = branches.slice(0, BRANCH_PREVIEW_COUNT);

  const borderColor = selected ? palette.accentBorder : "transparent";
  const showBranchPreview = preview.length > 0;
  const openPublicPage = () => navigate(publicHref);

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={openPublicPage}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openPublicPage();
        }
      }}
      aria-label={`${t("booking.organization.viewPublicPage")}: ${name}`}
      style={{
        background: selected ? palette.accentSoft : palette.surfaceAlt,
        border: `2px solid ${borderColor}`,
        borderRadius: 18,
        padding: 0,
        boxShadow: selected ? palette.shadowSoft : "none",
        transition: "background 0.15s ease, border-color 0.15s ease",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        cursor: "pointer",
      }}
    >
      {/* Top click target: select the whole org → Step 3 */}
      <div
        style={{
          minHeight: 92,
          background: img
            ? `linear-gradient(90deg, rgba(15,23,42,.72), rgba(15,23,42,.16)), url(${img})`
            : `linear-gradient(135deg, ${palette.accentSoft}, ${palette.surfaceHover})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderBottom: palette.border,
          position: "relative",
        }}
      >
        <span
          style={{
            position: "absolute",
            left: 14,
            bottom: -26,
            width: 58,
            height: 58,
            borderRadius: 16,
            background: palette.surface,
            border: palette.border,
            color: palette.accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.65rem",
            boxShadow: palette.shadowSoft,
            overflow: "hidden",
          }}
          aria-hidden="true"
        >
          <i className="las la-store" />
        </span>
        {branchCount > 0 ? (
          <span
            style={{
              position: "absolute",
              right: 12,
              bottom: 12,
              background: "rgba(15, 23, 42, 0.72)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,.22)",
              borderRadius: 999,
              padding: "5px 10px",
              fontSize: "0.78rem",
              fontWeight: 700,
              backdropFilter: "blur(8px)",
            }}
          >
            <i className="las la-map-marker-alt me-1" aria-hidden="true" />
            {t("booking.organization.branchCount", { count: branchCount })}
          </span>
        ) : null}
        <Link
          to={publicHref}
          onClick={(event) => event.stopPropagation()}
          title={t("booking.organization.viewPublicPage")}
          aria-label={t("booking.organization.viewPublicPage")}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.94)",
            color: "hsl(166 100% 28%)",
            border: "1px solid rgba(255,255,255,0.85)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            textDecoration: "none",
            boxShadow: "0 10px 22px rgba(0,0,0,0.18)",
          }}
        >
          <i className="las la-eye" aria-hidden="true" />
        </Link>
      </div>
      <div
        className="text-start w-100"
        style={{
          background: "transparent",
          border: "none",
          padding: "34px clamp(12px, 3vw, 16px) 12px clamp(12px, 3vw, 16px)",
          cursor: "pointer",
          color: palette.text,
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
        }}
      >
        <div className="d-flex align-items-start gap-3">
          {img ? (
            <span
              style={{
                width: "clamp(40px, 9vw, 48px)",
                height: "clamp(40px, 9vw, 48px)",
                borderRadius: 12,
                background: palette.surface,
                border: palette.border,
                display: "none",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <img
                src={img}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </span>
          ) : (
            <span
              style={{
                width: "clamp(40px, 9vw, 48px)",
                height: "clamp(40px, 9vw, 48px)",
                borderRadius: 12,
                background: palette.surface,
                border: palette.border,
                color: palette.accent,
                display: "none",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.4rem",
                flexShrink: 0,
              }}
            >
              <i className="las la-building" aria-hidden="true" />
            </span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              className="fw-700"
              style={{
                fontSize: "1.05rem",
                color: palette.text,
                lineHeight: 1.3,
                wordBreak: "break-word",
              }}
            >
              {name}
            </div>
            {branchCount > 0 ? (
              <div
                style={{
                  fontSize: "0.78rem",
                  color: palette.textMuted,
                  marginTop: 4,
                }}
              >
                <i
                  className="las la-map-marker-alt me-1"
                  aria-hidden="true"
                />
                {t("booking.organization.branchCount", { count: branchCount })}
              </div>
            ) : null}
            <div className="d-flex flex-wrap gap-2" style={{ marginTop: 8 }}>
              <span
                style={{
                  background: palette.accentSoft,
                  color: palette.accent,
                  borderRadius: 999,
                  padding: "4px 9px",
                  fontSize: "0.74rem",
                  fontWeight: 700,
                }}
              >
                {t("booking.organization.publicBookingBadge")}
              </span>
              {showBranchPreview ? (
                <span
                  style={{
                    color: palette.textMuted,
                    fontSize: "0.8rem",
                    padding: "4px 0",
                  }}
                >
                  {t("booking.organization.pickBranchHint")}
                </span>
              ) : null}
            </div>
          </div>
          {selected ? (
            <span
              aria-hidden="true"
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: palette.accent,
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.85rem",
                flexShrink: 0,
              }}
            >
              <i className="las la-check" />
            </span>
          ) : null}
        </div>
      </div>

      {/* Branch preview list (clickable shortcuts) */}
      {showBranchPreview ? (
        <div
          style={{
            padding: "0 10px 10px 10px",
            borderTop: palette.border,
            paddingTop: 10,
            display: "grid",
            gap: 4,
          }}
        >
          {preview.map((branch) => (
            <OrgBranchRow
              key={branch.id}
              palette={palette}
              branch={branch}
              lang={lang}
              onClick={() => onSelectBranch(org, branch)}
            />
          ))}
        </div>
      ) : null}

      {/* Footer CTA — commits the selection AND advances to the next step.
          (The card header is "soft select" so users can browse without
          committing; this button is the explicit "I'm done choosing" path.) */}
    </div>
  );
}

export function OrganizationStep({
  palette,
  lang,
  industry,
  organizations,
  loading,
  error,
  selectedId,
  onSelect,
  onSelectBranchShortcut,
  onBack,
  onRetry,
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search.trim()) return organizations;
    const q = search.toLowerCase().trim();
    return organizations.filter((org) => {
      const name = pickTranslation(org, lang).toLowerCase();
      const address = pickAddress(org).toLowerCase();
      return name.includes(q) || address.includes(q);
    });
  }, [organizations, search, lang]);

  return (
    <StepCard palette={palette}>
      <StepHeader
        palette={palette}
        title={t("booking.organization.title")}
        subtitle={t("booking.organization.subtitle")}
        accessory={
          industry ? (
            <span
              className="badge fw-500"
              style={{
                background: palette.accentSoft,
                color: palette.accent,
                padding: "8px 12px",
                fontSize: "0.8rem",
                borderRadius: 999,
              }}
            >
              {t("booking.organization.industryLabel")}: {pickTranslation(industry, lang)}
            </span>
          ) : null
        }
      />
      <div style={{ padding: `4px ${STEP_PAD.x} 10px ${STEP_PAD.x}` }}>
        <div
          className="booking-organization-hint"
          style={{
            background: palette.accentSoft,
            border: `1px solid ${palette.accentBorder}`,
            borderRadius: 14,
            padding: "12px 14px",
            color: palette.text,
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <span
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: palette.surface,
              color: palette.accent,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontSize: "1.2rem",
            }}
            aria-hidden="true"
          >
            <i className="las la-bolt" />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span className="fw-700 d-block" style={{ fontSize: "0.95rem" }}>
              {t("booking.organization.fastTitle")}
            </span>
            <span style={{ color: palette.textMuted, fontSize: "0.84rem", lineHeight: 1.4 }}>
              {t("booking.organization.fastSubtitle")}
            </span>
          </span>
        </div>
        {/* {showBeautyDemo ? (
          <div
            style={{
              marginTop: 10,
              background: palette.surfaceAlt,
              border: palette.border,
              borderRadius: 14,
              padding: "12px 14px",
              color: palette.text,
              display: "flex",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                width: 38,
                height: 38,
                borderRadius: 12,
                background: palette.accentSoft,
                color: palette.accent,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: "1.2rem",
              }}
              aria-hidden="true"
            >
              <i className="las la-magic" />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="fw-700 d-block" style={{ fontSize: "0.95rem" }}>
                {t("booking.organization.beautyDemoTitle")}
              </span>
            </span>
            <Link
              to={beautyDemoHref}
              className="pill fw-600"
              style={{
                background: palette.accent,
                color: "#fff",
                border: "none",
                padding: "9px 14px",
                textDecoration: "none",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                maxWidth: "100%",
              }}
            >
              {t("booking.organization.beautyDemoCta")}
              <ArrowRightIcon color="#fff" size={14} />
            </Link>
          </div>
        ) : null} */}
      </div>
      <div style={{ padding: `8px ${STEP_PAD.x} 4px ${STEP_PAD.x}` }}>
        <SearchInput
          palette={palette}
          value={search}
          onChange={setSearch}
          placeholder={t("booking.common.search")}
        />
      </div>
      <div
        style={{
          padding: `12px ${STEP_PAD.x} ${STEP_PAD.contentBottom} ${STEP_PAD.x}`,
        }}
      >
        {loading ? (
          <div className="row g-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="col-12 col-sm-6 col-xl-4">
                <div
                  style={{
                    height: 180,
                    borderRadius: 16,
                    background: palette.surfaceAlt,
                    border: palette.border,
                  }}
                />
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState
            palette={palette}
            icon="las la-exclamation-circle"
            title={t("booking.errors.loadOrganizations")}
            action={<RetryButton palette={palette} onClick={onRetry} />}
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            palette={palette}
            icon="las la-search-minus"
            title={t("booking.common.noResults")}
          />
        ) : (
          <div className="row g-3">
            {filtered.map((org) => (
              <div key={org.id} className="col-12 col-sm-6 col-xl-4">
                <OrgCard
                  palette={palette}
                  org={org}
                  lang={lang}
                  selected={selectedId === org.id}
                  onSelectBranch={(o, b) => {
                    if (typeof onSelectBranchShortcut === "function") {
                      onSelectBranchShortcut(o, b);
                    } else {
                      onSelect(o);
                    }
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      <StepFooter palette={palette}>
        <button
          type="button"
          onClick={onBack}
          className="pill fw-500"
          style={{
            background: "transparent",
            border: palette.border,
            color: palette.textMuted,
            padding: "8px 18px",
          }}
        >
          <i className="las la-arrow-left me-2" aria-hidden="true" />
          {t("booking.common.back")}
        </button>
      </StepFooter>
    </StepCard>
  );
}

/* =====================================================================
 * Step 3 — Branch
 * ===================================================================== */

export function BranchStep({
  palette,
  lang,
  organization,
  branches,
  loading,
  error,
  selectedId,
  onSelect,
  onBack,
  onNext,
  onRetry,
}) {
  const { t } = useTranslation();
  const orgName = organization ? pickTranslation(organization, lang) : "";

  return (
    <StepCard palette={palette}>
      <StepHeader
        palette={palette}
        title={t("booking.branch.title")}
        subtitle={t("booking.branch.subtitle", { org: orgName })}
      />
      <div
        style={{
          padding: `${STEP_PAD.contentTop} ${STEP_PAD.x} ${STEP_PAD.contentBottom} ${STEP_PAD.x}`,
        }}
      >
        {loading ? (
          <SkeletonRow palette={palette} count={3} />
        ) : error ? (
          <EmptyState
            palette={palette}
            icon="las la-exclamation-circle"
            title={t("booking.errors.loadBranches")}
            action={<RetryButton palette={palette} onClick={onRetry} />}
          />
        ) : branches.length === 0 ? (
          <EmptyState
            palette={palette}
            icon="las la-map-marker"
            title={t("booking.common.noResults")}
          />
        ) : (
          <div className="d-grid gap-2">
            {branches.map((branch) => {
              const name = pickTranslation(branch, lang);
              const address =
                pickAddress(branch) || t("booking.branch.addressFallback");
              return (
                <SelectableCard
                  key={branch.id}
                  palette={palette}
                  selected={selectedId === branch.id}
                  onClick={() => onSelect(branch)}
                  icon="las la-map-marker-alt"
                  title={name}
                  subtitle={address}
                  trailing={<ArrowRightIcon color={palette.textSubtle} size={18} />}
                />
              );
            })}
          </div>
        )}
      </div>
      <StepFooter palette={palette}>
        <button
          type="button"
          onClick={onBack}
          className="pill fw-500"
          style={{
            background: "transparent",
            border: palette.border,
            color: palette.textMuted,
            padding: "8px 18px",
          }}
        >
          <i className="las la-arrow-left me-2" aria-hidden="true" />
          {t("booking.common.back")}
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!selectedId}
          className="pill fw-500"
          style={{
            background: palette.accent,
            color: "#fff",
            border: "none",
            padding: "10px 24px",
            opacity: selectedId ? 1 : 0.5,
          }}
        >
          {t("booking.common.next")}
          <i className="las la-arrow-right ms-2" aria-hidden="true" />
        </button>
      </StepFooter>
    </StepCard>
  );
}

function EntryOption({ palette, icon, title, subtitle, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-start w-100"
      style={{
        background: palette.surface,
        border: palette.border,
        borderRadius: 16,
        padding: "16px 18px",
        color: palette.text,
        display: "flex",
        alignItems: "center",
        gap: 14,
        cursor: "pointer",
        transition: "background 0.15s ease, border-color 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = palette.surfaceAlt;
        e.currentTarget.style.border = `1px solid ${palette.accentBorder}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = palette.surface;
        e.currentTarget.style.border = palette.border;
      }}
    >
      <span
        style={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: palette.surfaceAlt,
          color: palette.accent,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.35rem",
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        <i className={icon} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="d-block fw-700" style={{ color: palette.text, fontSize: "1rem" }}>
          {title}
        </span>
        <span style={{ color: palette.textMuted, fontSize: "0.86rem", lineHeight: 1.4 }}>
          {subtitle}
        </span>
      </span>
      <ArrowRightIcon color={palette.textSubtle} size={18} />
    </button>
  );
}

export function BookingEntryChoiceStep({
  palette,
  organization,
  branch,
  lang,
  onChooseSpecialist,
  onChooseService,
  onBack,
}) {
  const { t } = useTranslation();
  const organizationName = organization ? pickTranslation(organization, lang) : "";
  const branchName = branch ? pickTranslation(branch, lang) : "";
  const branchAddress = branch ? pickAddress(branch, lang) : "";
  const displayName = organizationName || branchName;
  const displayMeta = branchAddress || branchName;
  const orgImage = resolveImageUrl(organization?.image_path);

  return (
    <StepCard
      palette={palette}
      style={{
        borderRadius: 18,
        boxShadow: palette.shadowSoft,
      }}
    >
      <div
        style={{
          padding: "clamp(18px, 4vw, 26px)",
          display: "grid",
          gap: 18,
        }}
      >
        <div className="d-flex align-items-start gap-3">
          <span
            style={{
              width: 62,
              height: 62,
              borderRadius: 18,
              background: orgImage
                ? `url(${orgImage}) center / cover`
                : palette.accentSoft,
              border: palette.border,
              color: palette.accent,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.8rem",
              flexShrink: 0,
              overflow: "hidden",
            }}
            aria-hidden="true"
          >
            {orgImage ? null : <i className="las la-store" />}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            {displayName ? (
              <h2
                className="mb-1"
                style={{
                  fontFamily: BOOKING_FONT_FAMILY,
                  fontWeight: 700,
                  color: palette.text,
                  fontSize: "clamp(1.25rem, 3vw, 1.7rem)",
                  lineHeight: 1.2,
                }}
              >
                {displayName}
              </h2>
            ) : null}
            {displayMeta ? (
              <div style={{ color: palette.textMuted, fontSize: "0.95rem" }}>
                <i className="las la-map-marker-alt me-1" aria-hidden="true" />
                {displayMeta}
              </div>
            ) : null}
          </div>
        </div>

        <div>
          <h3
            className="mb-1"
            style={{
              fontFamily: BOOKING_FONT_FAMILY,
              fontWeight: 700,
              color: palette.text,
              fontSize: "clamp(1.1rem, 2.6vw, 1.35rem)",
              lineHeight: 1.2,
            }}
          >
            {t("booking.entryChoice.title")}
          </h3>
          <p className="mb-0" style={{ color: palette.textMuted, fontSize: "0.92rem" }}>
            {t("booking.entryChoice.subtitle", { branch: branchName })}
          </p>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          <EntryOption
            palette={palette}
            icon="las la-user-friends"
            title={t("booking.entryChoice.specialistTitle")}
            subtitle={t("booking.entryChoice.specialistSubtitle")}
            onClick={onChooseSpecialist}
          />
          <EntryOption
            palette={palette}
            icon="las la-list-ul"
            title={t("booking.entryChoice.serviceTitle")}
            subtitle={t("booking.entryChoice.serviceSubtitle")}
            onClick={onChooseService}
          />
        </div>
      </div>
      <StepFooter palette={palette}>
        <button
          type="button"
          onClick={onBack}
          className="pill fw-500"
          style={{
            background: "transparent",
            border: palette.border,
            color: palette.textMuted,
            padding: "8px 18px",
          }}
        >
          <i className="las la-arrow-left me-2" aria-hidden="true" />
          {t("booking.common.back")}
        </button>
      </StepFooter>
    </StepCard>
  );
}

function WorkerAvatar({ palette, worker, fallbackIcon = "las la-user" }) {
  const image = resolveStorageUrl(worker?.image_path);
  const initials = worker?.name
    ? `${worker.name?.[0] || ""}${worker.surname?.[0] || ""}`.toUpperCase()
    : "";

  return (
    <span
      className="booking-worker-avatar"
      style={{
        width: 48,
        height: 48,
        borderRadius: "50%",
        background: image ? `url(${image}) center / cover` : palette.accentSoft,
        color: palette.accent,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: image ? 0 : initials ? "0.95rem" : "1.25rem",
        fontWeight: 700,
        fontFamily: BOOKING_FONT_FAMILY,
        flexShrink: 0,
        overflow: "hidden",
      }}
      aria-hidden="true"
    >
      {image ? null : initials || <i className={fallbackIcon} />}
    </span>
  );
}

function getWorkerName(worker) {
  if (!worker) return "";
  return [worker.name, worker.surname].filter(Boolean).join(" ").trim();
}

function getWorkerProfession(worker) {
  if (Array.isArray(worker?.professions) && worker.professions.length > 0) {
    return worker.professions.map((item) => item.name).filter(Boolean).join(", ");
  }
  return "";
}

function getWorkerServicesText(worker, t) {
  const count = Array.isArray(worker?.categories) ? worker.categories.length : 0;
  if (!count) return t("booking.specialists.noServices");
  return t("booking.specialists.servicesCount", { count });
}

function getWorkerAverageRating(worker) {
  const value = Number(worker?.average_rating);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function getWorkerRatingCount(worker) {
  const value = Number(worker?.rating_count);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function WorkerRatingSummary({ palette, worker }) {
  const average = getWorkerAverageRating(worker);
  const count = getWorkerRatingCount(worker);
  if (!average) return null;

  return (
    <span
      className="d-inline-flex align-items-center gap-1"
      style={{
        color: palette.textMuted,
        fontSize: "0.82rem",
        lineHeight: 1.2,
        marginTop: 4,
        fontFamily: BOOKING_FONT_FAMILY,
      }}
    >
      <i className="las la-star" style={{ color: "#fbbf24" }} aria-hidden="true" />
      <span
        style={{
          color: palette.text,
          fontWeight: 700,
          fontFamily: BOOKING_FONT_FAMILY,
        }}
      >
        {average.toFixed(2)}
      </span>
      {count ? <span>({count})</span> : null}
    </span>
  );
}

function RatingStars({ value, onChange, disabled }) {
  return (
    <span className="d-inline-flex align-items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          onClick={() => onChange(star)}
          aria-label={`${star}`}
          style={{
            border: "none",
            background: "transparent",
            padding: 2,
            cursor: disabled ? "default" : "pointer",
            color: star <= value ? "#fbbf24" : "#cbd5e1",
            fontSize: "1.9rem",
            lineHeight: 1,
          }}
        >
          <i className="las la-star" aria-hidden="true" />
        </button>
      ))}
    </span>
  );
}

function WorkerRatingPanel({
  palette,
  worker,
  orderTicketId,
  submitting,
  error,
  success,
  onSubmit,
  onClose,
}) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const workerName = getWorkerName(worker);
  const canSubmit = Boolean(orderTicketId && rating && !submitting);

  return (
    <div
      style={{
        border: palette.border,
        background: palette.surface,
        borderRadius: 16,
        padding: "16px clamp(14px, 3vw, 18px)",
        display: "grid",
        gap: 10,
        fontFamily: BOOKING_FONT_FAMILY,
      }}
    >
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div>
          <div
            style={{
              color: palette.text,
              fontWeight: 700,
              fontFamily: BOOKING_FONT_FAMILY,
            }}
          >
            {t("booking.feedback.title")}
          </div>
          <div style={{ color: palette.textMuted, fontSize: "0.88rem" }}>
            {workerName || t("booking.specialists.title")}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            border: palette.border,
            background: palette.surfaceAlt,
            color: palette.textMuted,
          }}
        >
          <i className="las la-times" aria-hidden="true" />
        </button>
      </div>

      <div className="text-center">
        <RatingStars value={rating} onChange={setRating} disabled={submitting} />
      </div>
      {!orderTicketId ? (
        <div
          style={{
            background: palette.surfaceAlt,
            border: palette.border,
            borderRadius: 12,
            padding: "12px 14px",
            color: palette.textMuted,
            fontSize: "0.88rem",
            lineHeight: 1.45,
          }}
        >
          <i className="las la-info-circle me-1" aria-hidden="true" />
          {t("booking.feedback.afterBooking")}
        </div>
      ) : null}
      {error ? (
        <div style={{ color: palette.danger || "#ef4444", fontSize: "0.84rem" }}>
          {error.message || t("booking.feedback.error")}
        </div>
      ) : null}
      {success ? (
        <div style={{ color: palette.success, fontSize: "0.84rem", fontWeight: 700 }}>
          {t("booking.feedback.success")}
        </div>
      ) : null}
      <button
        type="button"
        disabled={!canSubmit}
        onClick={() => onSubmit(rating)}
        className="pill fw-700"
        style={{
          justifySelf: "center",
          background: palette.accent,
          color: "#fff",
          border: "none",
          padding: "9px 18px",
          opacity: canSubmit ? 1 : 0.55,
          cursor: canSubmit ? "pointer" : "not-allowed",
        }}
      >
        {submitting ? t("booking.feedback.saving") : t("booking.feedback.submit")}
      </button>
    </div>
  );
}

function WorkerOption({
  palette,
  worker,
  title,
  subtitle,
  selected,
  onClick,
  onFeedback,
  feedbackLabel,
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      className="booking-worker-option text-start w-100"
      style={{
        background: selected ? palette.accentSoft : palette.surface,
        border: selected ? `2px solid ${palette.accentBorder}` : palette.border,
        borderRadius: 14,
        padding: "12px 14px",
        color: palette.text,
        display: "flex",
        alignItems: "center",
        gap: 12,
        cursor: "pointer",
        minHeight: 72,
        transition: "background 0.15s ease, border-color 0.15s ease",
        fontFamily: BOOKING_FONT_FAMILY,
      }}
      onMouseEnter={(e) => {
        if (selected) return;
        e.currentTarget.style.background = palette.surfaceAlt;
        e.currentTarget.style.border = `1px solid ${palette.accentBorder}`;
      }}
      onMouseLeave={(e) => {
        if (selected) return;
        e.currentTarget.style.background = palette.surface;
        e.currentTarget.style.border = palette.border;
      }}
    >
      <WorkerAvatar
        palette={palette}
        worker={worker}
        fallbackIcon="las la-user"
      />
      <span className="booking-worker-main" style={{ flex: 1, minWidth: 0 }}>
        <span
          className="booking-worker-title d-block"
          style={{
            color: palette.text,
            fontWeight: 700,
            fontFamily: BOOKING_FONT_FAMILY,
          }}
        >
          {title}
        </span>
        {subtitle ? (
          <span
            className="booking-worker-subtitle d-block"
            style={{ color: palette.textMuted, fontSize: "0.84rem", lineHeight: 1.35 }}
          >
            {subtitle}
          </span>
        ) : null}
        <WorkerRatingSummary palette={palette} worker={worker} />
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onFeedback?.(worker);
        }}
        className="booking-worker-feedback pill fw-700"
        aria-label={feedbackLabel}
        title={feedbackLabel}
        style={{
          background: palette.surfaceAlt,
          border: palette.border,
          color: palette.textMuted,
          padding: "7px 11px",
          fontSize: "0.78rem",
          flexShrink: 0,
          fontFamily: BOOKING_FONT_FAMILY,
        }}
      >
        <i className="las la-star me-1" style={{ color: "#fbbf24" }} aria-hidden="true" />
        <span className="booking-worker-feedback-label">{feedbackLabel}</span>
      </button>
      {selected ? (
        <span
          className="booking-worker-check"
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: palette.accent,
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: "0.82rem",
          }}
          aria-hidden="true"
        >
          <i className="las la-check" />
        </span>
      ) : null}
    </div>
  );
}

export function SpecialistSelectionStep({
  palette,
  workers,
  selectedWorker,
  orderTicketId,
  loading,
  error,
  title,
  subtitle,
  continueLabel,
  continueDisabled,
  onSelect,
  onBack,
  onRetry,
  onContinueServices,
  onSubmitRating,
}) {
  const { t } = useTranslation();
  const selectedKey = selectedWorker?.id ?? null;
  const [feedbackWorker, setFeedbackWorker] = useState(null);
  const [ratingState, setRatingState] = useState("idle");
  const [ratingError, setRatingError] = useState(null);

  const handleSubmitRating = async (rating) => {
    if (!feedbackWorker || !onSubmitRating) return;
    setRatingState("saving");
    setRatingError(null);
    try {
      await onSubmitRating({ worker: feedbackWorker, rating });
      setRatingState("success");
    } catch (err) {
      setRatingError(err);
      setRatingState("error");
    }
  };

  const feedbackPanel = feedbackWorker ? (
    <WorkerRatingPanel
      palette={palette}
      worker={feedbackWorker}
      orderTicketId={orderTicketId}
      submitting={ratingState === "saving"}
      success={ratingState === "success"}
      error={ratingError}
      onSubmit={handleSubmitRating}
      onClose={() => {
        setFeedbackWorker(null);
        setRatingState("idle");
        setRatingError(null);
      }}
    />
  ) : null;

  return (
    <StepCard
      palette={palette}
      style={{
        borderRadius: 18,
        boxShadow: palette.shadowSoft,
        display: "flex",
        flexDirection: "column",
        maxHeight: "min(78vh, 820px)",
      }}
    >
      <div
        className="booking-specialist-step-body"
        style={{
          padding: "clamp(18px, 4vw, 26px)",
          display: "grid",
          gap: 14,
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
          <div style={{ minWidth: 0 }}>
            <h2
              className="mb-1"
              style={{
                fontFamily: BOOKING_FONT_FAMILY,
                fontWeight: 700,
                color: palette.text,
                fontSize: "clamp(1.15rem, 2.6vw, 1.45rem)",
                lineHeight: 1.2,
              }}
            >
              {title || t("booking.specialists.title")}
            </h2>
            <p className="mb-0" style={{ color: palette.textMuted, fontSize: "0.92rem" }}>
              {subtitle || t("booking.specialists.subtitle")}
            </p>
          </div>
        </div>

        {loading ? (
          <SkeletonRow palette={palette} count={5} />
        ) : error ? (
          <EmptyState
            palette={palette}
            icon="las la-exclamation-circle"
            title={t("booking.specialists.error")}
            action={<RetryButton palette={palette} onClick={onRetry} />}
          />
        ) : (
          <div
            className="booking-specialist-scroll"
            style={{
              display: "grid",
              gap: 8,
              minHeight: 0,
              overflowY: workers.length > 6 ? "auto" : "visible",
              paddingRight: workers.length > 6 ? 6 : 0,
            }}
          >
            {feedbackPanel}
            {workers.length === 0 ? (
              <EmptyState
                palette={palette}
                icon="las la-user-clock"
                title={t("booking.specialists.empty")}
              />
            ) : (
              workers.map((worker) => {
                const profession = getWorkerProfession(worker);
                const serviceText = getWorkerServicesText(worker, t);
                const subtitle = [profession, serviceText].filter(Boolean).join(" • ");
                return (
                  <WorkerOption
                    key={worker.id}
                    palette={palette}
                    worker={worker}
                    selected={selectedKey === worker.id}
                    title={getWorkerName(worker)}
                    subtitle={subtitle}
                    onClick={() => onSelect(worker)}
                    onFeedback={setFeedbackWorker}
                    feedbackLabel={t("booking.feedback.button")}
                  />
                );
              })
            )}
          </div>
        )}
      </div>
      <StepFooter palette={palette}>
        <button
          type="button"
          onClick={onBack}
          className="pill fw-500"
          style={{
            background: "transparent",
            border: palette.border,
            color: palette.textMuted,
            padding: "8px 18px",
          }}
        >
          <i className="las la-arrow-left me-2" aria-hidden="true" />
          {t("booking.common.back")}
        </button>
        <button
          type="button"
          onClick={onContinueServices}
          disabled={continueDisabled}
          className="pill fw-500"
          style={{
            background: palette.accent,
            color: "#fff",
            border: "none",
            padding: "10px 24px",
            opacity: continueDisabled ? 0.5 : 1,
            cursor: continueDisabled ? "not-allowed" : "pointer",
          }}
        >
          {continueLabel || t("booking.entryChoice.continueToServices")}
          <i className="las la-arrow-right ms-2" aria-hidden="true" />
        </button>
      </StepFooter>
      <style>
        {`
          @media (max-width: 480px) {
            .booking-specialist-step-body {
              max-height: none;
              padding: 18px 16px 12px !important;
            }

            .booking-specialist-scroll {
              max-height: min(58dvh, 520px);
              overscroll-behavior: contain;
              -webkit-overflow-scrolling: touch;
              display: flex !important;
              flex-direction: column !important;
              gap: 10px !important;
              padding-right: 4px !important;
            }

            .booking-worker-option {
              position: relative !important;
              display: grid !important;
              grid-template-columns: 44px minmax(0, 1fr) 38px !important;
              align-items: center !important;
              gap: 10px !important;
              min-height: 86px !important;
              height: auto !important;
              padding: 13px 12px !important;
              flex: 0 0 auto !important;
            }

            .booking-worker-avatar {
              width: 44px !important;
              height: 44px !important;
              margin-top: 0 !important;
              grid-column: 1 !important;
            }

            .booking-worker-main {
              display: block !important;
              min-width: 0 !important;
              grid-column: 2 !important;
              padding-right: 0 !important;
            }

            .booking-worker-title {
              font-size: 0.95rem !important;
              line-height: 1.22 !important;
              overflow-wrap: anywhere !important;
            }

            .booking-worker-subtitle {
              font-size: 0.78rem !important;
              line-height: 1.3 !important;
              overflow-wrap: anywhere !important;
            }

            .booking-worker-feedback {
              position: static !important;
              grid-column: 3 !important;
              width: 36px !important;
              height: 36px !important;
              min-width: 36px !important;
              padding: 0 !important;
              border-radius: 50% !important;
              align-self: center !important;
              justify-content: center !important;
            }

            .booking-worker-feedback .las {
              margin: 0 !important;
              font-size: 1rem !important;
            }

            .booking-worker-feedback-label {
              display: none !important;
            }

            .booking-worker-check {
              position: absolute !important;
              right: 10px !important;
              top: auto !important;
              bottom: 8px !important;
              transform: none !important;
              width: 20px !important;
              height: 20px !important;
            }
          }

          @media (min-width: 481px) {
            .booking-specialist-scroll {
              max-height: min(52vh, 560px);
              overscroll-behavior: contain;
            }
          }

          .booking-specialist-scroll {
            scrollbar-width: thin;
            scrollbar-color: ${palette.accent} transparent;
          }

          .booking-specialist-scroll::-webkit-scrollbar {
            width: 7px;
          }

          .booking-specialist-scroll::-webkit-scrollbar-thumb {
            background: ${palette.accent};
            border-radius: 999px;
          }

          .booking-specialist-scroll::-webkit-scrollbar-track {
            background: transparent;
          }
        `}
      </style>
    </StepCard>
  );
}
